/**
 * ═══ READS ASKED IN THE SAME MOMENT GO AS ONE CALL (#1950) ═══
 *
 * #1920 counted ~34 calls on a coin open, 18 of them the coin page's own reads in its first 0.1 s. Here, under every
 * other wrapper of fetch (below the shared-read join, sharedReads.ts, so a read already out is joined first), a plain read
 * that may go in a batch (src/server/batchRules.ts) waits WINDOW_MS for others; the ones asked as the same person and
 * agent then go as one `GET /api/batch`, and each asker gets its own answer the moment the server sends that part
 * (src/server/routes/batchReads.ts), so the screen still draws piece by piece. A read alone in its moment goes out as
 * before. A write, sign-in, the live line, pictures and the reads the edge keeps never wait here.
 *
 * Each asker gets a real Response (status, headers, body) exactly as its own call would have given it; a part the
 * server could not ask, or a batch cut off, comes back as a read that never arrived (a rejected fetch), as before.
 * A batch the server does not answer as one (an older server during a deploy, a refusal) is asked again part by part.
 *
 * `localStorage["nmo.batch"] = "off"` sends every read on its own (for side-by-side counts).
 *
 * ═══ #3060: A READ THAT FAILS BECAUSE THE APP IS RESTARTING WAITS AND IS ASKED ONCE MORE, QUIETLY ═══
 *
 * During a restart on 4536 the owner's console showed "Uncaught (in promise) TypeError: Failed to fetch" from here
 * (alone → go → flush): a read handed back as failed to a screen that had no use for the failure. Now a read that could
 * not reach the app (or was cut off with its batch) waits for the app to answer again (server-wait.ts `waitForServer`,
 * the same wait the loading screen uses, which puts up the calm banner) and is asked ONCE more, back through the batch.
 * A screen that asks again itself on the app's return joins this read (sharedReads.ts) rather than sending a second.
 * Handed back as failed, as before: a read the asker stopped, a second failure, and any failure with the device offline
 * (that wait would never end).
 */
import { BATCH_KEPT_HEADER, BATCH_PATH, MOST_ADDRESS, MOST_PARTS, batchable } from "../server/batchRules.ts";
import { keepPart, keptPart } from "./keptParts.ts";
import { serverIsDown, waitForServer } from "./server-wait.ts";
import { isLinkMainRead } from "./linkFirst.ts";

/** How long the first read of a moment waits for others (the brief: one frame, ~10–20 ms). */
export const WINDOW_MS = 12;

interface Waiting {
  address: string;
  input: string | URL;
  init: RequestInit | undefined;
  resolve: (res: Response) => void;
  reject: (why: unknown) => void;
  /** #3060: already asked once more after the app came back; a second failure is handed back. */
  again?: boolean;
}

/** #3060: the fault for proof/3060 ("no_wait" or "all" in localStorage nmo.break3060): every failed read is handed straight back, as before. */
const breakFor = (): string | null => { try { return localStorage.getItem("nmo.break3060"); } catch { return null; } };

export function installBatchedReads(): void {
  if (typeof window === "undefined" || typeof window.fetch !== "function") return;
  try { if (localStorage.getItem("nmo.batch") === "off") return; } catch { /* no store: batch */ }
  const real = window.fetch.bind(window);
  /** By the asker's headers (sign-in, agent, accept): one batch carries one set. */
  const groups = new Map<string, { key: string; headers: Headers; waiting: Waiting[] }>();
  let timer: number | undefined;

  const alone = (one: Waiting): void => { real(one.input, one.init).then(one.resolve, (why: unknown) => failed(one, why)); };

  const stopped = (one: Waiting): unknown => one.init?.signal?.reason ?? new DOMException("The operation was aborted.", "AbortError");
  const enqueue = (one: Waiting, key: string, headers: Headers): void => {
    const group = groups.get(key) ?? { key, headers, waiting: [] };
    groups.set(key, group);
    group.waiting.push(one);
    timer ??= window.setTimeout(flush, WINDOW_MS);
  };
  /** #3060: a read that could not reach the app waits for it and goes once more (see the top); else it fails as before. */
  const failed = (one: Waiting, why: unknown, key?: string, headers?: Headers): void => {
    const signal = one.init?.signal;
    const offline = typeof navigator !== "undefined" && navigator.onLine === false;
    if (one.again === true || signal?.aborted === true || !(why instanceof TypeError) || offline || (breakFor() === "no_wait" || breakFor() === "all")) {
      /** Asked once more already: the app watch above (server-wait.ts) must not ask a third time. */
      if (one.again === true && why instanceof TypeError) Object.assign(why, { genieAskedAgain: true });
      one.reject(why);
      return;
    }
    one.again = true;
    signal?.addEventListener("abort", () => one.reject(stopped(one)), { once: true });
    void waitForServer().then(() => {
      if (signal?.aborted === true) return;
      if (key !== undefined && headers !== undefined) enqueue(one, key, headers);
      else alone(one);
    });
  };

  const flush = (): void => {
    timer = undefined;
    const all = [...groups.values()];
    groups.clear();
    /** #2174: the app went away while these waited: each fails here, as the network would, with no red line. */
    if (serverIsDown()) {
      for (const group of all) for (const one of group.waiting) failed(one, new TypeError("Failed to fetch"), group.key, group.headers);
      return;
    }
    for (const group of all) {
      /** A read stopped while it waited goes out on its own, so fetch says "stopped" the way it always does. */
      const live: Waiting[] = [];
      for (const one of group.waiting) (one.init?.signal?.aborted ? alone(one) : live.push(one));
      let batch: Waiting[] = [];
      let length = BATCH_PATH.length + 1;
      const go = (): void => {
        if (batch.length === 1) alone(batch[0]!);
        else if (batch.length > 1) void send(group.key, group.headers, batch);
        batch = [];
        length = BATCH_PATH.length + 1;
      };
      for (const one of live) {
        const adds = encodeURIComponent(one.address).length + 3;
        if (batch.length >= MOST_PARTS || length + adds > MOST_ADDRESS) go();
        batch.push(one);
        length += adds;
      }
      go();
    }
  };

  const send = async (key: string, asked: Headers, parts: Waiting[]): Promise<void> => {
    const address = BATCH_PATH + "?" + parts.map((one) => "r=" + encodeURIComponent(one.address)).join("&");
    /** #2481: each part's kept copy, named by its fingerprint; a part answered "unchanged" (304) is drawn from it. */
    const kept = parts.map((one) => keptPart(key + "\n" + one.address));
    const headers = new Headers(asked);
    if (kept.some((one) => one !== null)) headers.set(BATCH_KEPT_HEADER, JSON.stringify(kept.map((one) => one?.tag ?? null)));
    const left = new Map<number, Waiting>(parts.map((one, i) => [i, one]));
    /** A read stopped while the batch is out is told so at once; its part, when it comes, is dropped. */
    parts.forEach((one, i) => {
      one.init?.signal?.addEventListener("abort", () => {
        if (left.delete(i)) one.reject(one.init?.signal?.reason ?? new DOMException("The operation was aborted.", "AbortError"));
      }, { once: true });
    });
    let res: Response;
    try {
      res = await real(address, { method: "GET", headers, credentials: "same-origin" });
    } catch (why) {
      for (const one of left.values()) failed(one, why, key, asked);
      return;
    }
    /** #1941: refused for coming too fast: every part is told the same, never sent again one by one (that is 24 more asks). */
    if (res.status === 429) {
      const said = new Uint8Array(await res.arrayBuffer().catch(() => new ArrayBuffer(0)));
      const headers: Record<string, string> = { "content-type": res.headers.get("content-type") ?? "application/json" };
      const after = res.headers.get("retry-after");
      if (after !== null) headers["retry-after"] = after;
      for (const one of left.values()) answer(one, 429, headers, said);
      return;
    }
    if (res.status !== 200 || !res.body || !(res.headers.get("content-type") ?? "").startsWith("text/plain")) {
      void res.body?.cancel().catch(() => undefined);
      for (const one of left.values()) alone(one);
      return;
    }
    const reader = res.body.getReader();
    let held: Uint8Array = new Uint8Array(0);
    type Head = { i: number; status: number; headers: Record<string, string>; n: number };
    let head = null as Head | null;
    try {
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        held = joined(held, value);
        for (;;) {
          if (head === null) {
            const end = held.indexOf(10);
            if (end === -1) break;
            head = JSON.parse(new TextDecoder().decode(held.subarray(0, end))) as Head;
            held = held.subarray(end + 1);
          }
          if (held.length < head.n) break;
          const body = held.slice(0, head.n);
          held = held.subarray(head.n);
          const one = left.get(head.i);
          left.delete(head.i);
          const mine = kept[head.i] ?? null;
          if (one && head.status === 304 && mine !== null) answer(one, mine.status, { ...mine.headers, "x-genie-unchanged": "1" }, new TextEncoder().encode(mine.body));
          else if (one) {
            keepPart(key + "\n" + one.address, head.status, head.headers, body);
            answer(one, head.status, head.headers, body);
          }
          head = null;
        }
      }
    } catch { /* cut off: the parts still out are reads that never arrived (below) */ }
    for (const one of left.values()) failed(one, new TypeError("Failed to fetch"), key, asked);
  };

  window.fetch = (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const read = readOf(input, init);
    if (read === null) return real(input, init);
    return new Promise<Response>((resolve, reject) => {
      enqueue({ address: read.address, input: input as string | URL, init, resolve, reject }, read.key, read.headers);
    });
  };
}

function answer(one: Waiting, status: number, headers: Record<string, string>, body: Uint8Array): void {
  if (status === 0) { one.reject(new TypeError("Failed to fetch")); return; }
  const empty = status === 204 || status === 205 || status === 304;
  try {
    one.resolve(new Response(empty ? null : (body as BodyInit), { status, headers }));
  } catch (why) {
    one.reject(why);
  }
}

function joined(a: Uint8Array, b: Uint8Array): Uint8Array {
  if (a.length === 0) return b;
  const out = new Uint8Array(a.length + b.length);
  out.set(a);
  out.set(b, a.length);
  return out;
}

/** A plain read of this server that may go in a batch: its address and the headers it goes with; else null. */
function readOf(input: RequestInfo | URL, init?: RequestInit): { address: string; headers: Headers; key: string } | null {
  if (typeof input !== "string" && !(input instanceof URL)) return null;
  if ((init?.method ?? "GET").toUpperCase() !== "GET" || init?.body != null || init?.cache !== undefined || init?.keepalive) return null;
  if ((init?.credentials ?? "same-origin") !== "same-origin" || init?.mode !== undefined || init?.integrity !== undefined || init?.redirect !== undefined) return null;
  let url: URL;
  try { url = new URL(String(input), location.href); } catch { return null; }
  if (url.origin !== location.origin) return null;
  const address = url.pathname + url.search;
  if (!batchable(address)) return null;
  /** #2318: opened at a coin's or a trader's address, that page's own first answer goes on its own (linkFirst.ts). */
  if (isLinkMainRead(address)) return null;
  const headers = new Headers(init?.headers);
  const key = [...headers].map(([name, value]) => `${name}:${value}`).sort().join("\n");
  return { address, headers, key };
}
